import React from "react";
import { Card, Col, Row, Table, Badge } from "react-bootstrap";

/**
 * In-dashboard wiki: how third-party logistics (3PL) partners operate on
 * Lusuku — onboarding, staff/rider roles, dispatch, the fee split, COD,
 * and the partner-facing dashboard surfaces.
 *
 * IMPORTANT — content policy for this file:
 *   - No credentials, API keys, tokens, connection strings.
 *   - No internal hostnames or ports that aren't already public.
 *   - Development-only guidance must be gated behind NODE_ENV so CRA
 *     dead-code-eliminates it from the production bundle.
 */
const IS_DEV = process.env.NODE_ENV === "development";

const ACCENT = "#006202";
const TINT = "#0062021a";
const PILLTEXT = "#006202";
const MUTED = "#6b7a99";

const titleStyle = { fontSize: 13.5, fontWeight: 700, color: "#1a2b4a" };
const bodyText = { fontSize: 12.5 };
const listStyle = { fontSize: 12.5, paddingLeft: 18 };
const cardStyle = { border: "1px solid #e5e9f1", borderRadius: 8, boxShadow: "none" };
const tableStyle = { fontSize: 12 };
const theadStyle = { background: "#f5f7fb" };

const pillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.3px",
  textTransform: "uppercase",
  background: TINT,
  color: PILLTEXT,
  marginRight: 6,
  marginBottom: 4,
};

const Pill = ({ label }) => <span style={pillStyle}>{label}</span>;

const SectionIcon = ({ cls }) => (
  <i
    className={cls}
    style={{ fontSize: 15, color: "#1a2b4a", marginRight: 8, verticalAlign: "middle" }}
  />
);

const QUICK_ANSWERS = [
  [
    "A partner claims an order never reached their riders — what do I check?",
    "Open the order and confirm it actually reached PENDING_3PL for that partner (the handoff is explicit — a vendor or admin must toggle “Assign to 3PL Partner” and pick them). Then check the order’s assignment history: every offer, rejection, and timeout is recorded there. Also confirm the partner account is active and their riders are online near the pickup — assignment only offers to riders geo-near the pickup within the order’s zone.",
  ],
  [
    "A customer says the delivery fee looks wrong — can the app have miscalculated it?",
    "No. The fee is computed server-side from a signed quote, and order creation re-derives it while ignoring anything the client sends. To see exactly how a fee was built (zone, distance tiers, surge, complexity, floors), reproduce the trip in the Fee Transparency view’s live simulator.",
  ],
  [
    "Who is allowed to change delivery pricing?",
    "Only staff holding the settings:delivery_config:edit permission (super-admins pass automatically); anyone else is rejected with a 403. Every edit is audit-logged with a field-level old→new diff and takes effect immediately — no redeploy. The Fee Transparency view shows who last changed the config and when.",
  ],
  [
    "A rider says they were underpaid on a long trip.",
    "The split is cost-protected: the rider always receives at least the full modelled delivery cost, which on long trips is more than the nominal 42%. Pull the order’s settlement statement to see exactly who got what and confirm rider + partner + Lusuku sums to the fee.",
  ],
  [
    "A rider can’t take new orders after some cash deliveries.",
    "Their COD held balance has crossed the deposit threshold, which flags them until they deposit the held cash via mobile money through the Wallet service. The amount owed is server-derived from order totals — a rider typing a different number changes nothing.",
  ],
  [
    "A partner’s staff member says they can’t see Financials.",
    "Staff permissions are set per module (riders, orders, financials, tracking, profile, staff, analytics) at none / view / full by the partner themselves — point the partner at their Staff view to adjust the grid. The partner account always has full access.",
  ],
  [
    "An order has sat on ASSIGNED with no movement.",
    "The assigned rider hasn’t accepted yet. Riders who don’t respond within the timeout (default 60s) are auto-rejected and the order cascades to the next eligible rider — check the assignment history for the trail. If the partner has no responsive riders, staff can recall the order from the partner and reassign it.",
  ],
];

const LIFECYCLE = [
  ["PROCESSING", "Order confirmed; vendor/admin chooses rider or 3PL"],
  ["PENDING_3PL", "Handed to the partner company, no rider yet"],
  ["ASSIGNED", "Partner picks one of its riders"],
  ["ACCEPTED", "Rider accepts (atomic — first accept wins)"],
  ["PICKED_UP", "Rider has the goods from the vendor"],
  ["DISPATCHED", "In transit to the customer"],
  ["DELIVERED", "Customer has the order; settlement follows"],
];

const SURFACES = [
  ["Partner Dashboard", "Stats cards + charts"],
  ["Riders", "Add / edit riders, status toggle, one-time credentials modal, vehicle badges"],
  ["Orders", "Pending tab for handed-off orders, rider assignment, proximity display"],
  ["Earnings", "Split breakdown, payout history"],
  ["Staff / Profile", "Staff list & creation, permission grid, temp passwords, profile edit"],
  ["Live Tracking", "Live map from rider GPS socket events"],
  [
    "Fee Transparency",
    "Super-admin: cost + margin + editable pricing config; vendor: read-only breakdown + split + live fee simulator",
  ],
];

const ThreePLPlatformWiki = () => {
  return (
    <div>
      {/* Compact header */}
      <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 12, marginBottom: 18 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1a2b4a" }}>
          3PL Partners on Lusuku
        </div>
        <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>
          Staff reference for third-party logistics partners, their staff and riders:
          onboarding, dispatch &amp; assignment, the fee split, and the partner dashboard
          surfaces.
        </div>
      </div>

      {IS_DEV && (
        <div
          style={{
            background: "#fffdf5",
            border: "1px solid #f1e0a8",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12.5,
          }}
        >
          <div style={{ fontWeight: 700, color: "#7a5a00", marginBottom: 2 }}>
            Local development only
          </div>
          <div className="text-muted">
            Run the backend delivery-quote simulation script to print the fee breakdown,
            split, and balanced-journal reconciliation for a Kampala City Square &rarr;
            Ntinda order — the same engine used in production. Use it to verify pricing
            changes before they go live.
          </div>
        </div>
      )}

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-delivery-fast" />
            At a glance — what the 3PL system is
          </Card.Title>
          <div className="mb-2">
            <Pill label="Partner fleet" />
            <Pill label="Dispatch workflow" />
            <Pill label="Fair-share earnings" />
            <Pill label="Durable assignment" />
            <Pill label="Partner staff RBAC" />
          </div>
          <p className="text-muted mb-0" style={bodyText}>
            A <strong>3PL partner</strong> is an authorised third-party logistics company
            that brings its own riders to fulfil Lusuku deliveries. Last-mile has three
            fulfilment modes — a vendor&rsquo;s own riders, 3PL partner riders, and Lusuku
            dispatch — and every vertical (restaurant, retail, brand, packages) hands
            orders to 3PL through the same workflow, never a special case.
          </p>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-badge" />
                1. What a partner is &amp; onboarding
              </Card.Title>
              <p className="text-muted mb-2" style={bodyText}>
                Only Lusuku administrators create 3PL partner profiles — there is no
                public self-registration. Admin creates the partner with an email +
                password (bcrypt-hashed) and a welcome email delivers the login and
                dashboard URL.
              </p>
              <ul className="mb-0 text-muted" style={listStyle}>
                <li>
                  Partner logs in on the main dashboard, routed by{" "}
                  <code>operationMode: '3PL_PARTNER'</code>; the account must be{" "}
                  <code>active</code> to sign in. Partners register service areas and
                  hubs.
                </li>
                <li>
                  Every partner-scoped endpoint enforces ownership — a partner only ever
                  sees its own riders, orders, and payouts, and ownership is asserted on
                  every partner route.
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-single-02" />
                2. Partner staff &amp; rider roles
              </Card.Title>
              <p className="text-muted mb-2" style={bodyText}>
                Partners don&rsquo;t share their login. They create <strong>staff</strong>{" "}
                accounts with granular permissions, and <strong>riders</strong> who work
                through the Rider app.
              </p>
              <ul className="mb-0 text-muted" style={listStyle}>
                <li>
                  Staff permissions are per-module (<code>riders</code>,{" "}
                  <code>orders</code>, <code>financials</code>, <code>tracking</code>,{" "}
                  <code>profile</code>, <code>staff</code>, <code>analytics</code>) at{" "}
                  <em>none / view / full</em> levels; partners always have full access.
                </li>
                <li>
                  Staff sign in on the same login page; their token carries the permission
                  grid, which views check before rendering. Riders are created by the
                  partner with a temporary password (shown once in a credentials modal)
                  and a vehicle type.
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-send" />
            3. Dispatch &amp; assignment lifecycle
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            A vendor or admin opens the order, toggles{" "}
            <strong>&ldquo;Assign to 3PL Partner&rdquo;</strong>, picks the partner, and
            sends it; the partner then assigns one of its own riders from the partner
            dashboard.
          </p>
          <Row className="g-2">
            {LIFECYCLE.map(([state, note], i) => (
              <Col md={6} lg={4} key={state}>
                <div
                  style={{
                    background: "#f6faf6",
                    border: "1px solid #dceadb",
                    borderRadius: 8,
                    padding: 10,
                    height: "100%",
                  }}
                >
                  <div style={{ fontSize: 10.5, color: MUTED }}>STEP {i + 1}</div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 12.5,
                      color: ACCENT,
                      letterSpacing: "0.4px",
                    }}
                  >
                    {state}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 3 }}>
                    {note}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <ul className="mb-0 text-muted mt-3" style={listStyle}>
            <li>
              <strong>Durable scheduler.</strong> Assignment runs on a persistent job
              scheduler with atomic (compare-and-set) accept — not in-memory timers — so
              it survives restarts and scales across instances; eligible riders are found
              geo-near the pickup within the order&rsquo;s zone.
            </li>
            <li>
              <strong>Accept / reject / timeout.</strong> A rider who doesn&rsquo;t
              respond within the timeout (default 60s) is auto-rejected and the order
              cascades to the next rider; every offer and rejection lands in the
              order&rsquo;s assignment history. Staff can also recall an order from a
              partner before delivery.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-money-coins" />
            4. Earnings &amp; the fee split
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Delivery pricing is <strong>server-authoritative</strong>: the fee engine
            issues a signed quote (HMAC, 15-min TTL) and order creation re-derives the
            fee, <strong>ignoring</strong> any client-supplied <code>deliveryFee</code>/
            <code>totalPrice</code>. The fee is <em>base fare + cumulative distance
            tiers, times surge and complexity, plus a flat convenience fee</em> — then
            clamped so it never drops below cost &times; 1.20 (the margin floor) or the
            zone&rsquo;s minimum fee, and rounded to the nearest 500. Zones are
            distance-based: &le;12 km urban, &le;35 km peri-urban, beyond that upcountry
            (minimum fees 5,000 / 6,000 / 15,000 by default). Surge windows run on
            Africa/Kampala time (default AM 7&ndash;9h &times;1.2, PM 17&ndash;20h
            &times;1.3, capped at &times;1.5). All of it lives in the delivery-fee
            configuration, editable from the <strong>Fee Transparency</strong> view.
          </p>
          <p className="text-muted mb-2" style={bodyText}>
            The split is <strong>cost-protected fair-share</strong>: the rider always
            receives at least the full modelled delivery cost, and the guaranteed surplus
            is shared Lusuku/partner. On short, dense trips this degrades to the nominal
            rates (Lusuku 40% / partner 18% / rider 42%); on long trips the rider takes
            proportionally more. In-house vendor riders are split 10% Lusuku / 90% rider
            instead. The invariant <code>rider + partner + lusuku === fee</code> holds
            exactly, and every settlement posts a balanced double-entry journal to the
            Wallet ledger — unbalanced journals are rejected before write.
          </p>
          <Table responsive hover size="sm" className="mb-2" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Verified example (&asymp;7.3 km, Kampala City Square &rarr; Ntinda)</th>
                <th>Fee</th>
                <th>Rider</th>
                <th>Partner</th>
                <th>Lusuku</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>3PL, off-peak (urban)</td>
                <td><strong>UGX 8,000</strong></td>
                <td>6,004</td> <td>619</td> <td>1,377</td>
              </tr>
              <tr>
                <td>3PL, evening-peak surge &times;1.3</td>
                <td>UGX 10,000</td> <td>6,004</td> <td>1,240</td> <td>2,756</td>
              </tr>
              <tr>
                <td>Vendor&rsquo;s own rider, off-peak</td>
                <td>UGX 8,000</td> <td>7,200</td> <td>0</td> <td>800</td>
              </tr>
              <tr>
                <td>Upcountry reference (&asymp;80 km, Kampala &rarr; Jinja)</td>
                <td>UGX 31,000</td> <td>25,600</td> <td>1,676</td> <td>3,724</td>
              </tr>
            </tbody>
          </Table>
          <ul className="mb-0 text-muted" style={listStyle}>
            <li>
              The rider&rsquo;s 6,004 above <em>is</em> the full delivery cost — the cost
              floor lifted them above the nominal 42%, and the journal still balances to
              the fee exactly. Surge and complexity multipliers raise the fee (and the
              surplus split), never the rider floor.
            </li>
            <li>
              A per-order <strong>settlement statement</strong> (who got what — recorded
              vs ledger vs paid) is available for every delivered order, and
              reconciliation cross-checks collected fee == split == payouts, flagging
              mismatches.
            </li>
            <li>
              SLA: 45 minutes assignment-to-delivery by default; late penalty UGX 500/min{" "}
              <strong>capped at the rider&rsquo;s earnings</strong> (net earning never
              goes negative); UGX 1,000 early bonus.
            </li>
            <li>
              COD: the rider collects cash on Lusuku&rsquo;s behalf; the amount owed is
              server-derived from the order total, and a held-balance threshold forces a
              mobile-money deposit before taking more orders. Rider-app rates come from
              the backend — no hardcoded fee constants on the client.
            </li>
            <li>
              <strong>Governance:</strong> editing pricing requires the{" "}
              <code>settings:delivery_config:edit</code> permission (super-admins pass
              automatically); read views hide internal cost/margin from non-admins; every
              change is audit-logged with a field-level diff and a version bump, and
              applies immediately.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bullet-list-67" />
            5. Partner dashboard surface area
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            3PL partners have their own route group in the admin sidebar; the API groups
            behind it are <code>/api/3pl-partners</code> and <code>/api/3pl-staff</code>.
            Orders handed to a partner appear on their Orders view under the pending
            (<code>PENDING_3PL</code>) tab; live rider positions are on{" "}
            <code>/admin/rider-tracking</code>.
          </p>
          <Table responsive hover size="sm" className="mb-0" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Sidebar view</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {SURFACES.map(([view, notes]) => (
                <tr key={view}>
                  <td>{view}</td>
                  <td className="text-muted">{notes}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bulb-63" />
            6. Quick answers
          </Card.Title>
          {QUICK_ANSWERS.map(([q, a], i) => (
            <div
              key={q}
              style={{ padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid #eef1f6" }}
            >
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>{q}</div>
              <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>{a}</div>
            </div>
          ))}
          {IS_DEV && (
            <div className="mt-3">
              <Badge bg="secondary">DEV</Badge>{" "}
              <span className="text-muted" style={bodyText}>
                Pricing config changes apply immediately (no redeploy) — verify with the
                Fee Transparency simulator or the backend delivery-quote simulation
                script first.
              </span>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ThreePLPlatformWiki;
